import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, input, type OnInit, Output, signal } from '@angular/core';

export interface HeaderDropdownOption {
  value: string;
  label: string;
}

export interface HeaderDropdownConfig {
  /** Optional label shown before the select, e.g. "Plant" */
  label?: string;
  /** Set to false to hide this dropdown from the header. */
  enabled?: boolean;
  options: HeaderDropdownOption[];
  selected: string;
  onChange: (value: string) => void;
}

import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { NgxCaptureModule, NgxCaptureService } from 'ngx-capture';
import { filter, map, type Observable, of, switchMap } from 'rxjs';
import { AzureAuthService } from '../../auth';
@Component({
  selector: 'dtis-header',
  templateUrl: './dtis-header.component.html',
  providers: [],
  host: {
    class: 'print:hidden',
  },
  styles: [
    `
      .header-logo {
        background: url('/assets/dtis-schaeffler-header-logo.svg') no-repeat;
        height: 20px;
        width: 175px;
      }
      a:-webkit-any-link {
        color: #ffffffdd;
      }

      .slide-in-left-enter {
        transform: translateX(-100%);
        left: 0;
        animation: slideInLeftEnter 500ms ease-in forwards;
      }

      .slide-in-left-leave {
        transform: translateX(0px);
        animation: slideInLeftLeave 500ms ease-out forwards;
      }

      .text-fade-enter {
        opacity: 0;
        transform: translateX(-10%);
      }

      .text-fade-transition {
        opacity: 1;
        transform: translateX(-10%);
        animation: textFadeTransition 500ms ease-out forwards;
      }

      .text-fade-leave {
        opacity: 1;
        animation: textFadeLeave 500ms ease-out forwards;
      }

      @keyframes slideInLeftEnter {
        from {
          transform: translateX(-100%);
          left: 0;
        }
        to {
          transform: translateX(0px);
        }
      }

      @keyframes slideInLeftLeave {
        from {
          transform: translateX(0px);
        }
        to {
          transform: translateX(-100%);
          left: 0;
        }
      }

      @keyframes textFadeTransition {
        from {
          opacity: 1;
          transform: translateX(-10%);
        }
        to {
          opacity: 1;
          transform: translateX(0%);
        }
      }

      @keyframes textFadeLeave {
        from {
          opacity: 1;
        }
        to {
          opacity: 1;
        }
      }
    `,
  ],
  imports: [CommonModule, RouterModule, NgxCaptureModule, MatFormFieldModule, MatIconModule, MatSelectModule, MatTooltipModule, MatSnackBarModule],
})
export class DtisHeaderComponent implements OnInit {
  snackbar = inject(MatSnackBar);
  capture = inject(NgxCaptureService);
  dialog = inject(MatDialog);
  router = inject(Router);
  activated = inject(ActivatedRoute);
  titleService = inject(Title);
  auth = inject(AzureAuthService);
  /**
   * Can take a custome title
   */
  title = input.required<string>();
  /**
   * An Link to point the helper icons to
   * If not present the icon will not be rendered
   */
  helplink = input<string>();
  /**
   * An Link to point the helper icons to
   * If not present the icon will not be rendered
   */
  helplabel = input('Contact via Email');
  /**
   * indicates if the current route is the default route. To obtain if the navigation arrow should be displayed
   */
  isDefaultRoute = true;
  /**
   * used for route title
   */
  secondaryTitle = signal('');
  /**
   * Serves the azure profile picture
   */
  profilePicture$!: Observable<string>;
  /**
   *
   */
  fullTitle = computed(() => {
    const pageTitle = this.title();
    const secondaryTitle = this.secondaryTitle() ?? '';
    return `${pageTitle}  ${secondaryTitle}`;
  });
  /**
   * Contain the name of the current user
   */
  username$!: Observable<string>;
  /**
   * Contain the initials fo the current user
   */
  initalizes$!: Observable<unknown>;
  /**
   * The current environment loaded
   */
  environment!: string;
  /**
   *
   */
  @Output() toggleMenu = new EventEmitter<boolean>();
  /**
   *
   */
  header_links = input<
    {
      // biome-ignore lint/complexity/noBannedTypes: This is a common pattern for defining a list of header links, and the complexity is justified.
      callback?: Function;
      routerLink?: string[];
      tooltip: string;
      icon: string;
    }[]
  >([]);
  /**
   * Optional custom dropdown rendered in the header, e.g. for switching app context like "Plant".
   */
  headerDropdown = input<HeaderDropdownConfig | undefined>(undefined);
  /**
   * Additional custom dropdowns rendered alongside the primary header dropdown.
   */
  headerDropdowns = input<HeaderDropdownConfig[]>([]);
  /**
   * Combined header dropdowns in display order.
   */
  dropdowns = computed(() => {
    const primary = this.headerDropdown();
    const configuredDropdowns = primary ? [primary, ...this.headerDropdowns()] : this.headerDropdowns();
    return configuredDropdowns;
  });
  /**
   * Optional readonly label displayed next to the dropdown, e.g. for showing plant info.
   */
  headerReadonlyLabel = input<string | undefined>(undefined);
  /**
   * modifies the username to remap to initials
   */
  public ngOnInit(): void {
    // Case of overview page events
    this.#setTitleAndHeadline();
    this.profilePicture$ = this.auth.getProfileImage();
    this.initalizes$ = of(this.auth.handleAccount())?.pipe(
      map((username) => {
        if (!username?.name) {
          return '';
        }

        return username.name
          .split(', ')
          .map((username_part: string) => username_part.charAt(0))
          .reverse()
          .join()
          .toUpperCase()
          .replace(',', ''); // convert basically "Geissler, Benjamin SSAFSDSDSA" to "BG"}
      })
    );
  }

  /**
   * captures devices and navigationends events to display nicer titles with some informations
   */
  #setTitleAndHeadline() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        switchMap(() => this.activated.children),
        switchMap((r) => r.data)
      )
      .subscribe((event) => {
        // biome-ignore lint/complexity/useLiteralKeys: event is of type Data, which is just a map of key value pairs, so we have to use the string keys to access the data
        this.secondaryTitle.set(event['label']);
        // biome-ignore lint/complexity/useLiteralKeys: event is of type Data, which is just a map of key value pairs, so we have to use the string keys to access the data
        this.isDefaultRoute = event['defaultRoute'];
        this.titleService.setTitle(this.fullTitle());
      });
  }
}
